import { cardContext, type cardContextValues } from '@context/card-context';
import { CardHeaderEditContext } from '@context/card-header-edit/provider';
import { cardPresetContext } from '@context/card-preset-context';
import useRemoteImageBlob from '@hooks/use-remote-image-blob';
import { useCallback, useContext, useMemo } from 'react';
import { type CardImage, CardLayout } from 'types';

import { getImgUrls } from '../CardHeader';
import { type ImageType, getSuggestedLayout, hasLayouts, imagesInLayout, typeToFieldId } from '../utilities';
import { PicturePreview, PicturePreviewContainer } from './PicturePreview';

interface ImagesSubsectionProps {
  disabled?: boolean | undefined;
  isPreset: boolean;
  popoverNode: HTMLElement | undefined;
  profilePictureImg?: CardImage | null;
  companyLogoImg?: CardImage | null;
  coverPhotoImg?: CardImage | null;
  onPopoverClose?: () => void;
  /**
   * This prop will only be defined when we need to track the transition
   * from a static card to a card that is being edited by the user
   * (eg. desktop onboarding flow)
   */
  onUserStartedAddingImages?: () => void;
}

const CardImagesSubsection: React.FC<React.PropsWithChildren<ImagesSubsectionProps>> = ({
  isPreset,
  popoverNode,
  coverPhotoImg,
  companyLogoImg,
  profilePictureImg,
  onUserStartedAddingImages = undefined,
}) => {
  const context_preset = useContext(cardPresetContext);
  const context_card = useContext(cardContext);
  const context = isPreset ? context_preset : context_card;
  const cardId = context_card.cardId;
  const presetId = context_preset.presetId;
  const userId = context_card.userId;
  const orgId = context_preset.orgId;
  let linkedAttributes: string[] = [];
  if (isPreset) {
    linkedAttributes = (context as cardContextValues).cardFields.linkedAttributes;
  }
  const profileBlob = useRemoteImageBlob(profilePictureImg?.originalImageUrl);
  const logoBlob = useRemoteImageBlob(companyLogoImg?.originalImageUrl);
  const coverBlob = useRemoteImageBlob(coverPhotoImg?.originalImageUrl);

  const originalImgBlobs = useMemo(() => {
    return {
      cover: coverBlob,
      profile: profileBlob,
      logo: logoBlob,
    };
  }, [coverBlob, logoBlob, profileBlob]);

  const [cardHeaderEditState, editDispatch] = useContext(CardHeaderEditContext);
  const { profilePicturePreviewUrl, logoImagePreviewUrl, coverPhotoPreviewUrl, picturePreview } =
    cardHeaderEditState;

  const { cardFields, cardFieldsDispatch, lockedFields, toggleFieldLock, canLock } = context;
  const onRemove = useCallback(
    (imageType: ImageType) => {
      if (imageType === 'profile') {
        const layout = getSuggestedLayout({
          cover: coverPhotoImg?.editedImageUrl,
          profile: false,
          logo: companyLogoImg?.editedImageUrl,
        });
        cardFieldsDispatch({ type: 'REMOVE_PROFILE_PICTURE', layout });
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'profile_picture_img' });

        originalImgBlobs.profile.updateImageUrl(undefined);
        editDispatch({ type: 'SET_PROFILE_PICTURE_PREVIEW_URL', value: '' });

        if (lockedFields.includes(typeToFieldId('profile'))) toggleFieldLock(typeToFieldId('profile'), false);
      } else if (imageType === 'logo') {
        const layouts = hasLayouts({
          cover: coverPhotoImg?.editedImageUrl,
          profile: profilePictureImg?.editedImageUrl,
          logo: false,
        });
        cardFieldsDispatch({ type: 'REMOVE_COMPANY_LOGO', layout: layouts[layouts.length - 1] });
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'company_logo_img' });

        originalImgBlobs.logo.updateImageUrl(undefined);
        editDispatch({ type: 'SET_LOGO_IMAGE_PREVIEW_URL', value: '' });

        if (lockedFields.includes(typeToFieldId('logo'))) toggleFieldLock(typeToFieldId('logo'), false);
      } else if (imageType === 'cover') {
        const layouts = hasLayouts({
          cover: false,
          profile: profilePictureImg?.editedImageUrl,
          logo: companyLogoImg?.editedImageUrl,
        });
        cardFieldsDispatch({ type: 'REMOVE_COVER_PHOTO', layout: layouts[layouts.length - 1] });
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'cover_photo_img' });
        originalImgBlobs.cover.updateImageUrl(undefined);
        editDispatch({ type: 'SET_COVER_PHOTO_PREVIEW_URL', value: '' });

        if (lockedFields.includes(typeToFieldId('cover'))) toggleFieldLock(typeToFieldId('cover'), false);
      }
    },
    [
      cardFieldsDispatch,
      companyLogoImg,
      coverPhotoImg,
      originalImgBlobs,
      lockedFields,
      profilePictureImg,
      toggleFieldLock,
      editDispatch,
    ],
  );

  const onSave = useCallback(
    (image: CardImage, type: ImageType) => {
      if (type === 'profile') {
        cardFieldsDispatch({
          type: 'SET_PROFILE_PICTURE',
          value: image,
          layout: cardFields.layout,
        });
        // reset editing info
        editDispatch({ type: 'SET_PROFILE_PICTURE_PREVIEW_URL', value: '' });
      } else if (type === 'logo') {
        cardFieldsDispatch({ type: 'SET_COMPANY_LOGO', value: image, layout: cardFields.layout });
        // reset editing info
        editDispatch({ type: 'SET_LOGO_IMAGE_PREVIEW_URL', value: '' });
      } else if (type === 'cover') {
        cardFieldsDispatch({ type: 'SET_COVER_PHOTO', value: image, layout: cardFields.layout });
        // reset editing info
        editDispatch({ type: 'SET_COVER_PHOTO_PREVIEW_URL', value: '' });
      }
    },
    [cardFieldsDispatch, editDispatch, cardFields.layout],
  );

  const onSelect = useCallback(
    (image: CardImage, type: ImageType) => {
      image.editorMetadata ??= {};
      image.editorMetadata.editorVersion = 'web_selector_v1'; // ensure local images aren't rendered in backwards compat.
      if (image.originalImageUrl) {
        if (type === 'profile') {
          const layout = getSuggestedLayout({
            cover: coverPhotoImg?.editedImageUrl,
            profile: true,
            logo: companyLogoImg?.editedImageUrl,
          });
          editDispatch({ type: 'SET_PROFILE_PICTURE_PREVIEW_URL', value: image.originalImageUrl });
          cardFieldsDispatch({
            type: 'SET_PROFILE_PICTURE',
            value: image,
            layout,
          });
        } else if (type === 'logo') {
          const layout = getSuggestedLayout({
            cover: coverPhotoImg?.editedImageUrl,
            profile: profilePictureImg?.editedImageUrl,
            logo: true,
          });
          editDispatch({ type: 'SET_LOGO_IMAGE_PREVIEW_URL', value: image.originalImageUrl });
          cardFieldsDispatch({
            type: 'SET_COMPANY_LOGO',
            value: image,
            layout,
          });
        } else if (type === 'cover') {
          const layout = getSuggestedLayout({
            cover: true,
            profile: profilePictureImg?.editedImageUrl,
            logo: companyLogoImg?.editedImageUrl,
          });
          editDispatch({ type: 'SET_COVER_PHOTO_PREVIEW_URL', value: image.originalImageUrl });
          cardFieldsDispatch({
            type: 'SET_COVER_PHOTO',
            value: image,
            layout,
          });
        }
      }
    },
    [coverPhotoImg, companyLogoImg, profilePictureImg, cardFieldsDispatch, editDispatch],
  );

  const onUpdateLogo = useCallback(
    (image: CardImage, quality: 'high' | 'low' | undefined) => {
      editDispatch({
        type: 'SET_LOGO_IMAGE_PREVIEW_URL',
        value: image.editedImageUrl || '',
        quality,
      });
    },
    [editDispatch],
  );
  const onUpdateProfile = useCallback(
    (image: CardImage, quality: 'high' | 'low' | undefined) => {
      editDispatch({
        type: 'SET_PROFILE_PICTURE_PREVIEW_URL',
        value: image.editedImageUrl || '',
        quality,
      });
    },
    [editDispatch],
  );
  const onUpdateCover = useCallback(
    (image: CardImage, quality: 'high' | 'low' | undefined) => {
      editDispatch({
        type: 'SET_COVER_PHOTO_PREVIEW_URL',
        value: image.editedImageUrl || '',
        quality,
      });
    },
    [editDispatch],
  );

  const onSelectOpen = useCallback(
    (value: ImageType) => {
      if (onUserStartedAddingImages) {
        onUserStartedAddingImages();
      }

      editDispatch({ type: 'SET_PICTURE_PREVIEW', value });
    },
    [editDispatch, onUserStartedAddingImages],
  );
  const onPopoverClose = useCallback(() => {
    editDispatch({ type: 'RESET' });
  }, [editDispatch]);

  const profileLocked = context.layoutImagesLocked || lockedFields.includes(typeToFieldId('profile'));
  const images = useMemo(() => {
    const urls = getImgUrls(
      false,
      {
        profilePictureImg,
        companyLogoImg,
        coverPhotoImg,
      },
      {
        profilePicturePreviewUrl,
        coverPhotoPreviewUrl,
        logoImagePreviewUrl,
      },
      {
        left: picturePreview === 'profile',
        right: picturePreview === 'logo',
      },
      originalImgBlobs,
      cardFields.layout || CardLayout.Layout_1A,
    );

    return urls;
  }, [
    cardFields.layout,
    originalImgBlobs,
    profilePictureImg,
    companyLogoImg,
    coverPhotoImg,
    profilePicturePreviewUrl,
    logoImagePreviewUrl,
    coverPhotoPreviewUrl,
    picturePreview,
  ]);

  const imagesRequired = cardFields.layout ? imagesInLayout(cardFields.layout) : [];

  return (
    <PicturePreviewContainer>
      <PicturePreview
        isLocked={lockedFields.includes(typeToFieldId('logo')) || context.layoutImagesLocked}
        canLock={canLock}
        cardFieldsDispatch={cardFieldsDispatch}
        image={images.logo}
        isPreset={isPreset}
        linkedAttributes={linkedAttributes}
        onRemove={() => onRemove('logo')}
        onSave={(i) => onSave(i, 'logo')}
        onSelect={(s) => onSelect(s, 'logo')}
        onSelectOpen={onSelectOpen}
        onPopoverClose={onPopoverClose}
        onUpdate={onUpdateLogo}
        required={false} // logo is never required.
        targetNode={popoverNode}
        toggleFieldLock={toggleFieldLock}
        type="logo"
        userId={userId}
        cardId={cardId}
        presetId={presetId}
        orgId={orgId}
      />

      {!isPreset && (
        <PicturePreview
          isLocked={profileLocked}
          canLock={canLock}
          cardFieldsDispatch={cardFieldsDispatch}
          image={images.profile}
          isPreset={isPreset}
          linkedAttributes={linkedAttributes}
          onRemove={() => onRemove('profile')}
          onSave={(i) => onSave(i, 'profile')}
          onSelect={(s) => onSelect(s, 'profile')}
          onSelectOpen={onSelectOpen}
          onPopoverClose={onPopoverClose}
          onUpdate={onUpdateProfile}
          required={imagesRequired.includes('profile')}
          targetNode={popoverNode}
          toggleFieldLock={toggleFieldLock}
          type="profile"
          userId={userId}
          cardId={cardId}
          presetId={presetId}
          orgId={orgId}
        />
      )}

      <PicturePreview
        isLocked={lockedFields.includes(typeToFieldId('cover')) || context.layoutImagesLocked}
        canLock={canLock}
        cardFieldsDispatch={cardFieldsDispatch}
        image={images.cover}
        isPreset={isPreset}
        linkedAttributes={linkedAttributes}
        onRemove={() => onRemove('cover')}
        onSave={(i) => onSave(i, 'cover')}
        onSelect={(s) => onSelect(s, 'cover')}
        onSelectOpen={onSelectOpen}
        onPopoverClose={onPopoverClose}
        onUpdate={onUpdateCover}
        required={false} // cover is never required
        targetNode={popoverNode}
        toggleFieldLock={toggleFieldLock}
        type="cover"
        userId={userId}
        cardId={cardId}
        presetId={presetId}
        orgId={orgId}
      />
    </PicturePreviewContainer>
  );
};

export default CardImagesSubsection;
